<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container grouphr-frame-card">
      <div class="grouphr-frame-header">
        <div>
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ title }}
          </oxd-text>
          <oxd-text tag="p" class="grouphr-frame-subtitle">
            {{ subtitle }}
          </oxd-text>
        </div>
      </div>
      <oxd-divider />
      <iframe
        ref="frame"
        class="grouphr-frame"
        :style="{height: `${frameHeight}px`}"
        :src="frameUrl"
        :title="title"
        loading="lazy"
        scrolling="auto"
        @load="handleFrameLoad"
      ></iframe>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GroupHRFrame',
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    frameUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      frameHeight: 720,
    };
  },
  mounted() {
    window.addEventListener('message', this.handleFrameMessage);
    window.setTimeout(this.requestFrameHeight, 100);
    window.setTimeout(this.requestFrameHeight, 500);
    window.setTimeout(this.requestFrameHeight, 1200);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleFrameMessage);
  },
  methods: {
    handleFrameLoad() {
      if (this.correctNestedFrameNavigation()) return;
      window.setTimeout(this.requestFrameHeight, 120);
      window.setTimeout(this.requestFrameHeight, 600);
    },
    correctNestedFrameNavigation() {
      const frame = this.$refs.frame;
      if (!frame || !frame.contentWindow) return false;
      let currentUrl = null;
      try {
        currentUrl = new URL(frame.contentWindow.location.href);
      } catch (error) {
        return false;
      }
      if (!currentUrl.pathname.includes('/index.php/time/groupHR')) {
        return false;
      }

      const embeddedUrl = new URL(this.frameUrl, window.location.origin);
      currentUrl.searchParams.forEach((value, key) => {
        if (key !== 'embedded') embeddedUrl.searchParams.set(key, value);
      });
      embeddedUrl.searchParams.set('embedded', '1');
      if (frame.src !== embeddedUrl.href) {
        frame.src = embeddedUrl.href;
        return true;
      }
      return false;
    },
    requestFrameHeight() {
      const frame = this.$refs.frame;
      if (!frame || !frame.contentWindow) return;
      frame.contentWindow.postMessage(
        {type: 'GROUPHR_REQUEST_HEIGHT'},
        window.location.origin,
      );
    },
    handleFrameMessage(event) {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type !== 'GROUPHR_FRAME_HEIGHT') return;
      const nextHeight = Number(data.height);
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
      const normalizedHeight = Math.min(20000, Math.max(560, Math.ceil(nextHeight)));
      if (normalizedHeight < this.frameHeight && data.allowShrink !== true) return;
      if (Math.abs(normalizedHeight - this.frameHeight) < 12) return;

      const scrollPositions = this.captureScrollPositions();
      this.frameHeight = normalizedHeight;
      this.$nextTick(() => {
        this.restoreScrollPositions(scrollPositions);
      });
    },
    captureScrollPositions() {
      const positions = [{target: window, left: window.scrollX, top: window.scrollY}];
      const selectors = [
        'html',
        'body',
        '.oxd-layout',
        '.oxd-layout-container',
        '.oxd-layout-context',
        '.orangehrm-background-container',
      ];
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(element => {
          positions.push({
            target: element,
            left: element.scrollLeft,
            top: element.scrollTop,
          });
        });
      });
      return positions;
    },
    restoreScrollPositions(positions) {
      positions.forEach(position => {
        if (position.target === window) {
          window.scrollTo(position.left, position.top);
          return;
        }
        position.target.scrollLeft = position.left;
        position.target.scrollTop = position.top;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.grouphr-frame-card {
  padding: 0;
  overflow: hidden;
  overflow-anchor: none;
}

.grouphr-frame-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem 1.5rem 0.5rem;
}

.grouphr-frame-subtitle {
  margin-top: 0.35rem;
  color: $oxd-interface-gray-color;
  font-size: 0.85rem;
}

.grouphr-frame {
  display: block;
  width: 100%;
  border: 0;
  background: #f4f6f8;
  overflow: auto;
  overflow-anchor: none;
}

@media (max-width: 800px) {
  .grouphr-frame-header {
    padding: 1rem 1rem 0.4rem;
  }
}
</style>
